import { CreditCardIcon, GiftIcon, PaperAirplaneIcon, PlusIcon, TrashIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { ChangeEventHandler, memo, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../components/Card';
import { ListItem } from '../../components/ListItem';
import { DotsPopover } from '../../components/Popover/DotsPopover';
import { Input } from '../../components/inputs/Input';
import { UserContext } from '../../contexts/UserContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import useOpen from '../../hooks/useOpen';
import { OrganisationDeleteModal } from '../../modules/organisation/OrganisationDeleteModal';
import { CreditsModal } from '../../modules/superadmin/CreditsModal';
import { ReferralsModal } from '../../modules/superadmin/ReferralsModal';
import { CreateCustomerOrganisationModal, CustomerOrganisationValues } from '../../modules/organisation/customers/CreateCustomerOrganisationModal';
import { InviteUsersModal } from '../../modules/organisation/customers/InviteUsersModal';
import { Button } from '../../components/Button';
import { useReferralRoleTranslations } from '../../hooks/translations/useReferralRoleTranslations';
import { OrganisationsPerMonthModal } from '../../modules/superadmin/OrganisationsPerMonthModal';
import { DEFAULT_ORGANISATION_FILTERS, OrganisationFilterBar, OrganisationFilterValues } from '../../modules/superadmin/OrganisationFilterBar';
import { T, useT } from '../../translation/src';
import { ReferralRole, SuperadminOrganisationsQuery, useSuperadminOrganisationCreateMutation, useSuperadminOrganisationsQuery } from '../../types/graphqlTypes';
import { cutPercent } from '../../modules/organisation/referrals/partnerRoles';
import { StatsProperty, SubscriptionPill, useStatisticsTranslations } from '../../components/Stats';
import { DowngradeTag, SubscriptionStatusTags, subscriptionStatusesOf } from '../../components/SubscriptionStatusTags';
import { AdminSectionLinks } from './AdminSectionLinks';

type Organisation = SuperadminOrganisationsQuery['superadminOrganisations']['data'][number];

interface OrganisationCardProps {
  organisation: Organisation;
}

interface OrganisationCardMenuProps extends OrganisationCardProps {
  openCreditsModal: () => void;
  openOrganisationDeleteModal: () => void;
  openReferralsModal: () => void;
  openInviteUsersModal: () => void;
}

// Only the menu *items* mount when the popover opens — keeping the per-card fiber cost low.
// The modals themselves live in OrganisationCard so they survive the popover closing: every
// ListItem is a Headless UI CloseButton, so clicking one closes the popover (and unmounts this
// menu). If the modals lived here too, they'd unmount on the very click meant to open them.
const OrganisationCardMenu: React.FC<OrganisationCardMenuProps> = ({ organisation, openCreditsModal, openOrganisationDeleteModal, openReferralsModal, openInviteUsersModal }) => {
  const onNavigateToOrganisation = useCallback(() => {
    window.open(`/organisations/${organisation.id}`, '_blank');
  }, [organisation.id]);

  return (
    <>
      <ListItem onClick={openCreditsModal} icon={CreditCardIcon}>
        <T _str="subscription" swc />
      </ListItem>
      <ListItem onClick={openReferralsModal} icon={GiftIcon}>
        <T _str="partner program" swc />
      </ListItem>
      <ListItem onClick={openInviteUsersModal} icon={UserPlusIcon}>
        <T _str="invite people" swc />
      </ListItem>
      <ListItem onClick={onNavigateToOrganisation} icon={PaperAirplaneIcon}>
        <T _str="Go to organisation" swc />
      </ListItem>
      <ListItem onClick={openOrganisationDeleteModal} icon={TrashIcon}>
        <T _str="Delete organisation" swc />
      </ListItem>
    </>
  );
};

export const OrganisationCard_: React.FC2<OrganisationCardProps> = ({ organisation }) => {
  const translations = useStatisticsTranslations();
  const roles = useReferralRoleTranslations();
  const referralTitle = useT('partner role', { swc: true });
  const acquiredByTitle = useT('acquired by', { swc: true });
  const managedByTitle = useT('managed by', { swc: true });
  const billedToTitle = useT('billed to', { swc: true });
  const { onClose: closeCreditsModal, open: creditsModalOpen, onOpen: openCreditsModal } = useOpen();
  const { onClose: closeOrganisationDeleteModal, open: organisationDeleteModalOpen, onOpen: openOrganisationDeleteModal } = useOpen();
  const { onClose: closeReferralsModal, open: referralsModalOpen, onOpen: openReferralsModal } = useOpen();
  const { onClose: closeInviteUsersModal, open: inviteUsersModalOpen, onOpen: openInviteUsersModal } = useOpen();

  const limitsInitialValues = useMemo(() => {
    const { customLimits } = organisation.subscription;
    return {
      monthlyCredits: customLimits?.monthlyCredits ?? 0,
      // Superadmin fields are in euros; the API speaks cents.
      projectUnitPrice: (customLimits?.projectUnitPrice ?? 0) / 100,
      subscriptionPrice: (customLimits?.subscriptionPrice ?? 0) / 100,
      // A pending downgrade is what the superadmin last decided — show that, not the plan still running.
      subscriptionType: organisation.subscription.scheduledType ?? organisation.subscription.type,
    };
  }, [organisation.subscription]);

  return (
    <Card noPadding>
      <div className="relative flex items-center bg-gray dark:bg-gray-900">
        <span className="p-4">{organisation.name}</span>
        <SubscriptionStatusTags statuses={subscriptionStatusesOf(organisation.subscription)} />
        <DowngradeTag scheduledType={organisation.subscription.scheduledType} />
        <DotsPopover>
          {/* Render-prop form: menu items only mount while the popover is open. */}
          {({ open }) =>
            open ? (
              <OrganisationCardMenu
                organisation={organisation}
                openCreditsModal={openCreditsModal}
                openOrganisationDeleteModal={openOrganisationDeleteModal}
                openReferralsModal={openReferralsModal}
                openInviteUsersModal={openInviteUsersModal}
              />
            ) : null
          }
        </DotsPopover>
      </div>
      <div className="flex flex-col px-4 py-2 divide-y divide-gray-700">
        <StatsProperty title={translations.projects} count={organisation.stats.totalProjects} nrRemaining={organisation.credits.balance} />
        <StatsProperty title={translations.subscription}>
          <span className="flex items-center gap-2">
            <SubscriptionPill subscription={organisation.subscription} />
            {/* Invoiced to a partner, not to the organisation: said where the plan is. */}
            {organisation.referral.billedTo && (
              <span className="text-xs text-gray-500">
                <T _str="via {partner}" args={{ partner: organisation.referral.billedTo.name }} />
              </span>
            )}
          </span>
        </StatsProperty>
        {/* Flex is pay-as-you-go — no period, no end date to show. */}
        {organisation.subscription.endDate && <StatsProperty title={translations.subscriptionEndDate} day={organisation.subscription.endDate} />}
        {/* A running term without its Odoo subscription: for sales to link (see the credits modal). */}
        {organisation.subscription.endDate && !organisation.odoo.orderId && (
          <StatsProperty title="Odoo">
            <span className="text-warning-yellow-700 dark:text-warning-yellow-500">
              <T _str="subscription not linked" />
            </span>
          </StatsProperty>
        )}
        {/* Only what is out of the ordinary: a partner and its cuts, and the partners the organisation is linked to. */}
        {organisation.referral.role !== ReferralRole.Customer && (
          <StatsProperty title={referralTitle}>
            {roles[organisation.referral.role]} · {cutPercent(organisation.referral.firstCutBasisPoints)} / {cutPercent(organisation.referral.recurringCutBasisPoints)} %
          </StatsProperty>
        )}
        {organisation.referral.acquiredBy && <StatsProperty title={acquiredByTitle}>{organisation.referral.acquiredBy.name}</StatsProperty>}
        {organisation.referral.managedBy && <StatsProperty title={managedByTitle}>{organisation.referral.managedBy.name}</StatsProperty>}
        {organisation.referral.billedTo && <StatsProperty title={billedToTitle}>{organisation.referral.billedTo.name}</StatsProperty>}
        <StatsProperty title={translations.joinedDate} date={organisation.createdAt} />
        <StatsProperty title={translations.users} count={organisation.stats.totalUsers} />
        <StatsProperty title={translations.pointCount} count={organisation.stats.totalPointCount} />
        <StatsProperty title="ID">{organisation.id}</StatsProperty>
      </div>
      <CreditsModal
        open={creditsModalOpen}
        onClose={closeCreditsModal}
        organisationId={organisation.id}
        organisationName={organisation.name}
        odoo={organisation.odoo}
        balance={organisation.credits.balance}
        nextExpiry={organisation.credits.nextExpiry}
        projectUnitPrice={organisation.subscription.projectUnitPrice}
        subscriptionType={organisation.subscription.type}
        scheduledSubscriptionType={organisation.subscription.scheduledType}
        scheduledAt={organisation.subscription.scheduledAt}
        subscriptionStartDate={organisation.subscription.startDate}
        subscriptionEndDate={organisation.subscription.endDate}
        subscriptionPausedAt={organisation.subscription.pausedAt}
        monthlyAllowance={organisation.credits.monthlyAllowance}
        planInitialValues={limitsInitialValues}
        billedTo={organisation.referral.billedTo}
      />
      <OrganisationDeleteModal open={organisationDeleteModalOpen} onClose={closeOrganisationDeleteModal} organisation={organisation} />
      <ReferralsModal open={referralsModalOpen} onClose={closeReferralsModal} organisationId={organisation.id} organisationName={organisation.name} referral={organisation.referral} />
      <InviteUsersModal open={inviteUsersModalOpen} onClose={closeInviteUsersModal} organisationId={organisation.id} organisationName={organisation.name} />
    </Card>
  );
};
const OrganisationCard = memo(OrganisationCard_);

// Defer mounting OrganisationCard until it scrolls near the viewport. Mirrors LazyProjectCard:
// cards mount lazily and stay mounted, and the intersection callback flips state inside
// startTransition so mount work is concurrent and yields to scroll/input.
const LazyOrganisationCard_: React.FC<OrganisationCardProps> = ({ organisation }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            startTransition(() => setMounted(true));
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted]);

  if (mounted) return <OrganisationCard organisation={organisation} />;
  // Placeholder keeps grid layout stable until the card mounts.
  return <div ref={ref} className="w-full h-[480px] rounded-xl bg-gray-100 dark:bg-[#242424]" aria-hidden />;
};
const LazyOrganisationCard = memo(LazyOrganisationCard_);

const useTranslations = () => {
  const searchOrganisation = useT('search organisation', { swc: true });
  return { searchOrganisation };
};

const PAGE_SIZE = 30;

// Loads the next page once it comes within reach of the end of the grid.
const LoadMoreSentinel: React.FC<{ onLoadMore: () => void }> = ({ onLoadMore }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && onLoadMore(), { rootMargin: '800px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onLoadMore]);
  return <div ref={ref} className="h-px" aria-hidden />;
};

export const AdminCenter: React.FC = () => {
  const loadingText = useT('loading...');
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<OrganisationFilterValues>(DEFAULT_ORGANISATION_FILTERS);
  const debouncedName = useDebouncedValue(name);
  const queryFilters = useMemo(() => ({ ...filters, name: debouncedName || undefined }), [debouncedName, filters]);
  const {
    data: superadminOrganisationsData,
    loading: organisationsLoading,
    fetchMore,
  } = useSuperadminOrganisationsQuery({
    variables: { pagination: { limit: PAGE_SIZE }, filters: queryFilters },
    notifyOnNetworkStatusChange: true,
  });
  const organisations = superadminOrganisationsData?.superadminOrganisations.data || [];
  const organisationTotal = superadminOrganisationsData?.superadminOrganisations.total;
  const nextCursor = superadminOrganisationsData?.superadminOrganisations.hasMore ? superadminOrganisationsData.superadminOrganisations.nextCursor : null;
  // The cache policy merges the pages (see apollo/cache.ts).
  const onLoadMore = useCallback(() => {
    if (!nextCursor || organisationsLoading) return;
    void fetchMore({ variables: { pagination: { limit: PAGE_SIZE, cursor: nextCursor }, filters: queryFilters } });
  }, [fetchMore, nextCursor, organisationsLoading, queryFilters]);
  const { onClose: closePerMonthModal, open: perMonthModalOpen, onOpen: openPerMonthModal } = useOpen();
  const translations = useTranslations();
  const currentUser = useContext(UserContext);
  const { onClose: closeCreateModal, open: createModalOpen, onOpen: openCreateModal } = useOpen();
  const [createOrganisation] = useSuperadminOrganisationCreateMutation({ refetchQueries: ['superadminOrganisations'], awaitRefetchQueries: true });
  const onCreate = useCallback(
    async ({ name, emails }: CustomerOrganisationValues) => {
      await createOrganisation({ variables: { name, emails } });
    },
    [createOrganisation],
  );

  const onChangeFilterName: ChangeEventHandler<HTMLInputElement> = useCallback((event) => setName(event.target.value), []);

  if (!currentUser?.isSuperAdmin) return null;

  return (
    <div className="flex flex-col w-full h-full px-14 dark:bg-[#313131] dark:text-white">
      <div className="flex items-center w-full pt-8 text-xl">
        <T _str="admin center" swc />
        <AdminSectionLinks />
      </div>
      <hr className="my-4 border-t border-gray-300 dark:border-gray-400" />
      <div className="flex flex-wrap items-center gap-4 mt-5">
        <Input className="w-52" placeholder={`${translations.searchOrganisation}...`} onChange={onChangeFilterName} />
        <OrganisationFilterBar value={filters} onChange={setFilters} />
        {/* The count of what the filters match; opens the new organisations per month. */}
        {organisationTotal != null && (
          <button type="button" onClick={openPerMonthModal} className="self-center text-sm text-gray-600 underline dark:text-gray-300 hover:text-neon-green-700 dark:hover:text-neon-green-300">
            <T _str="{count} organisations" args={{ count: organisationTotal }} />
          </button>
        )}
        <div className="ml-auto">
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon className="w-4 mr-1" />
            <T _str="organisation" swc />
          </Button>
        </div>
      </div>
      <OrganisationsPerMonthModal open={perMonthModalOpen} onClose={closePerMonthModal} filters={queryFilters} />
      <CreateCustomerOrganisationModal open={createModalOpen} onClose={closeCreateModal} title={<T _str="add organisation" swc />} onCreate={onCreate} />
      <div className="w-full h-full mt-10 overflow-auto">
        {/* Only the first load replaces the grid. Refetches (after a grant or plan change)
            must update the cards in place, or an open modal unmounts with its card. */}
        {organisationsLoading && !superadminOrganisationsData ? (
          <div>{loadingText}</div>
        ) : (
          <>
            <div className="grid w-full gap-4 rounded-md grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {organisations.map((organisation) => (
                <LazyOrganisationCard key={organisation.id} organisation={organisation} />
              ))}
            </div>
            {nextCursor && <LoadMoreSentinel onLoadMore={onLoadMore} />}
          </>
        )}
      </div>
    </div>
  );
};
